import { useCallback } from 'react'
import * as Sentry from '@sentry/nextjs'
import {
  clearLinkingFailure,
  getActiveEntitlementId,
  isEntitlementIdConsumed,
  isKnownLinkingReason,
  normalizeLinkingReason,
  readEntitlementIdFromSearch,
  setLinkingFailure,
} from '@/utils/activation'
import { markEntitlementConsumed } from '@/utils/activation/entitlementStore'
import { readActivationEnabled } from './useActivationEnabled'
import { IAuthResponse } from '@/types/auth'

/**
 * The two linking-outcome fields of an auth response (PRO-14 / PRO-9), read
 * off whatever envelope a flow uses. Aliased from `IAuthResponse` so the
 * response type stays the single source — both are `unknown` there because
 * neither shape is contractual; only `orderLinked` truthiness and a
 * normalized `orderLinkedReason` are consumed.
 */
type ILinkingFields = Pick<IAuthResponse, 'orderLinked' | 'orderLinkedReason'>

interface IUseActivationEntitlementResult {
  /**
   * The id to attach to an auth payload right now, or null. Null whenever the
   * scene matching the current pathname does not enable activation (per-scene
   * AuthActor script `options.activation.enabled` — see
   * `useActivationEnabled`): this is the choke point that keeps the feature
   * inert outside the flagged scenes even when a crafted URL carries the
   * param.
   * Otherwise URL first — the param in the address bar beats a stored record,
   * which keeps two tabs holding different ids each correct — then the store
   * (which is what survives the password-recovery bounce into a fresh tab).
   * The URL branch still defers to a consumed tombstone: nuqs keeps the param
   * in the address bar after a successful auth, and an already-linked id must
   * not ride the next auth on this tab. A getter rather than a render value
   * on purpose: storage must never be read during render (hydration
   * mismatch), only inside submit handlers.
   */
  getEntitlementId: () => string | null
  /**
   * The whole client-side footprint of the activation payload (PRO-14) in one
   * call, so each auth mutation stays a one-liner: runs `exec` with the id to
   * attach (or `undefined` — the request is then exactly what it was before
   * this integration existed), and consumes that id only on a **truthy**
   * `orderLinked`. Truthy on purpose — the success shape is not contractual:
   * the fx-npm-auth 2.4.0 source reads as a `data`-object passthrough while
   * the deployed dev backend answers the literal `true` (verified live
   * 2026-08-17); only the literal `false` (linking failure) is manufactured.
   *
   * `false` is the PRO-15 path: the backend names why in `orderLinkedReason`
   * (PRO-9) and the failure lands in the in-memory linking-failure store for
   * the AuthActor modal to render. The record is **never consumed here** —
   * a terminal reason tombstones only when the user acknowledges the modal
   * (`consumeLinkingFailure`), so a failure that was never surfaced (embedded
   * panels suppress the modal) keeps the pre-PRO-15 retry-on-next-auth
   * behavior. Every failure also reaches Sentry (warning, latched per
   * source+reason per page load, unknown tokens bucketed) as the ops signal
   * for a LINKING_UNAVAILABLE spike. A truthy outcome clears any pending
   * failure: a successful retry must not surface a stale message.
   *
   * *Absent* keeps the record too, because a backend without the linking
   * hook live (deploy order — the hook ships in fx-npm-auth 2.4.0, not 2.3.2
   * as the ticket says — or an endpoint that ignores the field, like
   * `/login/sso`) returns success without the field, and consuming on that
   * would silently strand the activation for the whole TTL —
   * `captureEntitlementId` is a no-op for the same unexpired id, so not even
   * re-clicking the partner link could recover it. The absent case is a
   * broken contract, so it reports to Sentry; the worst cost of keeping the
   * record is an idempotent re-link on the next auth. `readLinkingFields`
   * adapts responses that carry the outcome somewhere other than the top
   * level (register's envelope). `source` names the auth flow on the Sentry
   * report: `/login/sso` ignores the field *by design* (its providers
   * hardcode their own linking), so without the tag that permanent stream is
   * indistinguishable from the deploy-window signal docs/ACTIVATION.md says
   * to watch. Reports latch once per source per page load — a user retrying
   * a submit must not burn quota on the same broken contract.
   */
  withEntitlement: <T>(
    exec: (entitlementId?: string) => Promise<T>,
    options: {
      /** Auth flow tag for Sentry: 'login' | 'register' | 'register-auto-login' | 'firebase' | 'sso' */
      source: string
      readLinkingFields?: (result: T) => ILinkingFields | null | undefined
    }
  ) => Promise<T>
}

const readTopLevelLinkingFields = (result: unknown): ILinkingFields | null | undefined =>
  result as ILinkingFields | null | undefined

// One report per auth flow per page load: a retried submit repeats the same
// broken contract, and /login/sso would otherwise stream forever by design.
const warnedSources = new Set<string>()

// Same latch idea for linking failures, keyed source + bucketed reason. The
// bucketing (unknown tokens collapse to 'unknown') bounds the key space at
// 5 sources × 8 values — the raw reason is backend-controlled, and a backend
// that varied it per attempt would otherwise mint a fresh latch entry (and a
// fresh Sentry report) on every retry.
const warnedFailures = new Set<string>()

/**
 * Consumer API for the activation `entitlementId` (PRO-13). `useAuth` wraps
 * its login/register/sso mutations in `withEntitlement`, which attaches the
 * id to the payload, consumes it only when it actually rode a successful
 * request (PRO-14), and records a linking failure for the AuthActor modal to
 * surface (PRO-15). Inert end to end unless the cinematic enables activation.
 */
export const useActivationEntitlement = (): IUseActivationEntitlementResult => {
  const getEntitlementId = useCallback((): string | null => {
    if (typeof window === 'undefined') return null
    if (!readActivationEnabled()) return null

    const urlId = readEntitlementIdFromSearch(window.location.search)

    if (urlId && !isEntitlementIdConsumed(urlId)) return urlId

    return getActiveEntitlementId()
  }, [])

  const withEntitlement = useCallback(async <T,>(
    exec: (entitlementId?: string) => Promise<T>,
    options: { source: string, readLinkingFields?: (result: T) => ILinkingFields | null | undefined }
  ): Promise<T> => {
    const { source, readLinkingFields = readTopLevelLinkingFields } = options
    const entitlementId = getEntitlementId() ?? undefined
    const result = await exec(entitlementId)

    if (entitlementId) {
      const { orderLinked, orderLinkedReason } = readLinkingFields(result) ?? {}

      if (orderLinked) {
        markEntitlementConsumed(entitlementId)
        clearLinkingFailure()
      } else if (orderLinked === false) {
        const reason = normalizeLinkingReason(orderLinkedReason)
        const reasonBucket = isKnownLinkingReason(reason) ? reason : 'unknown'

        setLinkingFailure({ reason, entitlementId, source })

        const latchKey = `${source}:${reasonBucket}`

        if (!warnedFailures.has(latchKey)) {
          warnedFailures.add(latchKey)
          Sentry.captureException(
            new Error('Activation entitlement linking failed'),
            {
              level: 'warning',
              tags: { activationAuthFlow: source, activationLinkingReason: reasonBucket },
              extra: { orderLinkedReason: reason },
            }
          )
        }
      } else if (orderLinked == null && !warnedSources.has(source)) {
        // == null: `null` is the same broken contract as absent — fx-npm-auth
        // never emits it, so a data:null passthrough must warn, not vanish.
        warnedSources.add(source)
        Sentry.captureException(
          new Error('Auth response carried no orderLinked for an attached activation entitlementId'),
          { level: 'warning', tags: { activationAuthFlow: source } }
        )
      }
    }

    return result
  }, [getEntitlementId])

  return { getEntitlementId, withEntitlement }
}
